import { groupBy, min } from 'lib/array/arrayUtils'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import { diffInDays } from 'lib/datetime/dateUtils'
import { applyChannelMarkupAndRoundUpToTen, applyChannelMarkupToCombinedRoomsAndRoundUpToTen } from './channelMarkup'
import { resolveChannelMarkupDetails, resolveMemberChannelMarkupDetails } from './channelMarkupV2'
import type { ChannelMarkupSource, ChannelMarkupV2OfferPricing } from './channelMarkupV2'

/**
 * One priced option of an offer, as the lead-package rule reads it. Prices are the base
 * (before channel markup), as public-offer returns them.
 */
export interface ChannelMarkupLeadOption {
  duration: number
  price?: number | null
  luxPlusPrice?: number | null
  surcharge?: number | null
  isSoloTraveller: boolean
  /** Whether the option is one of the offer's `defaultOptions` (public-offer's lowest options). */
  isDefault: boolean
}

/**
 * Pick the lead option of one duration with the same rule as `getLowestPricePackage`, so the lead
 * is the package the offer tile's "from" price shows for that duration:
 *
 * 1. the offer's default options, while the duration has one;
 * 2. solo-traveller rates set aside, while another option is left;
 * 3. the lowest `(luxPlusPrice || price) + surcharge`.
 *
 * Every LE option carries its LuxPlus+ price whoever is looking, so the lead does not depend on
 * the viewer. Options without a price are skipped.
 */
function getLeadOption(options: ReadonlyArray<ChannelMarkupLeadOption>): ChannelMarkupLeadOption | undefined {
  const priced = options.filter(option => (option.price ?? 0) > 0)
  const defaults = priced.filter(option => option.isDefault)
  const candidates = defaults.length > 0 ? defaults : priced
  const nonSolo = candidates.filter(option => !option.isSoloTraveller)
  return min(
    nonSolo.length > 0 ? nonSolo : candidates,
    option => (option.luxPlusPrice || option.price || 0) + (option.surcharge ?? 0) || Number.MAX_SAFE_INTEGER,
  )
}

/**
 * The prices a v2 price-band condition reads for an LE offer (Flash, LPC), per duration.
 *
 * Product rule: a price band is checked on the offer's lead package only — the package the offer
 * tile's "from" price shows for that duration — and, if the lead qualifies, the markup or markdown
 * applies to every package of the offer for that duration. So every surface (tiles, offer page,
 * calendar, checkout) decides on this one number instead of the price it is showing, and the
 * decision is the same wherever the offer is priced.
 *
 * Built from public-offer's static options, which do not change when a package sells out, so a
 * sold-out lead does not flip the decision mid-booking. A duration with no priced option has no
 * entry; reading it gives no price, which fails a price band closed.
 */
export function getChannelMarkupLeadPricingByDuration(
  options: Array<ChannelMarkupLeadOption>,
): App.ChannelMarkupLeadPricingByDuration {
  const leadByDuration: App.ChannelMarkupLeadPricingByDuration = {}
  for (const [duration, durationOptions] of groupBy(options, option => option.duration)) {
    const lead = getLeadOption(durationOptions)
    if (lead?.price) {
      leadByDuration[duration] = { total: lead.price, luxPlusPrice: lead.luxPlusPrice || undefined }
    }
  }
  return leadByDuration
}

/**
 * The lead pricing for one duration of an offer, or `undefined` when the offer has none for it
 * (a price band then fails closed).
 */
export function getChannelMarkupLeadPricing(
  offer: { channelMarkupLeadPricing?: App.ChannelMarkupLeadPricingByDuration } | undefined,
  duration: number | undefined,
): App.ChannelMarkupLeadPricing | undefined {
  if (!offer?.channelMarkupLeadPricing || duration === undefined) {
    return undefined
  }
  return offer.channelMarkupLeadPricing[duration]
}

/** Whether an offer type's v2 price bands are checked on its lead package (Flash, LPC). */
export function isLeadPackageMarkupOfferType(
  offerType: string | undefined,
): offerType is typeof OFFER_TYPE_HOTEL | typeof OFFER_TYPE_ALWAYS_ON {
  return offerType === OFFER_TYPE_HOTEL || offerType === OFFER_TYPE_ALWAYS_ON
}

/**
 * {@link getChannelMarkupLeadPricing} for any loaded offer shape; only Flash/LPC offers carry lead
 * pricing, so any other offer has none.
 */
export function getOfferChannelMarkupLeadPricing(
  offer: App.Offer | App.OfferSummary | undefined,
  duration: number | undefined,
): App.ChannelMarkupLeadPricing | undefined {
  return offer && 'channelMarkupLeadPricing' in offer ? getChannelMarkupLeadPricing(offer, duration) : undefined
}

/**
 * The prices a v2 price band reads for one priced item: the lead package for an LE offer (Flash,
 * LPC), the item's own base prices for any other type (bedbank). Display and checkout both pick
 * their band prices here, so the markup a surface shows and the identity checkout sends are
 * decided on the same number.
 */
export function getChannelMarkupBandPricing(
  offerType: string | undefined,
  { leadPricing, ownPricing }: {
    leadPricing: App.ChannelMarkupLeadPricing | undefined
    ownPricing: ChannelMarkupV2OfferPricing | undefined
  },
): ChannelMarkupV2OfferPricing | undefined {
  return isLeadPackageMarkupOfferType(offerType) ? leadPricing : ownPricing
}

function markSuggestedTravelDates(
  metaData: App.OfferListMetaData,
  offerType: App.OfferType,
  offer: App.Offer | App.OfferSummary | undefined,
  markupSource: ChannelMarkupSource | undefined,
): App.OfferSuggestedDates | undefined {
  const suggestedTravelDates = metaData.suggestedTravelDates
  if (!suggestedTravelDates?.checkIn || !suggestedTravelDates.checkOut) {
    return suggestedTravelDates
  }
  const nights = diffInDays(new Date(suggestedTravelDates.checkIn), new Date(suggestedTravelDates.checkOut))
  const leadPricing = getOfferChannelMarkupLeadPricing(offer, nights)
  const markupDetails = resolveChannelMarkupDetails(metaData.offerId, offerType, markupSource, leadPricing)
  const memberMarkupDetails = resolveMemberChannelMarkupDetails(metaData.offerId, offerType, markupSource, leadPricing)
  return {
    ...suggestedTravelDates,
    price: suggestedTravelDates.price ? applyChannelMarkupAndRoundUpToTen(suggestedTravelDates.price, markupDetails) : suggestedTravelDates.price,
    memberPrice: suggestedTravelDates.memberPrice ? applyChannelMarkupAndRoundUpToTen(suggestedTravelDates.memberPrice, memberMarkupDetails) : suggestedTravelDates.memberPrice,
  }
}

/**
 * Mark a Flash/LPC search result's prices, which the search mapper stores before channel markup
 * (see `App.OfferListMetadataPricing['channelMarkupRoomCount']`). A v2 price band reads the
 * offer's lead package for the result's duration, so the tile's search price carries the same
 * markup as the offer page and checkout. Without the offer a price band fails closed; any other
 * condition still resolves. A result that is not deferred is returned as is.
 */
export function applyLeadChannelMarkupToSearchMetaData(
  metaData: App.OfferListMetaData,
  offer: App.Offer | App.OfferSummary | undefined,
  markupSource: ChannelMarkupSource | undefined,
): App.OfferListMetaData {
  const { pricing, type: offerType } = metaData
  const roomCount = pricing.channelMarkupRoomCount
  if (roomCount === undefined || !isLeadPackageMarkupOfferType(offerType)) {
    return metaData
  }
  const leadPricing = getOfferChannelMarkupLeadPricing(offer, pricing.duration)
  const markupDetails = resolveChannelMarkupDetails(metaData.offerId, offerType, markupSource, leadPricing)
  const memberMarkupDetails = resolveMemberChannelMarkupDetails(metaData.offerId, offerType, markupSource, leadPricing)
  return {
    ...metaData,
    suggestedTravelDates: markSuggestedTravelDates(metaData, offerType, offer, markupSource),
    pricing: {
      ...pricing,
      lowestPrice: pricing.lowestPrice ? applyChannelMarkupToCombinedRoomsAndRoundUpToTen(pricing.lowestPrice, markupDetails, roomCount) : pricing.lowestPrice,
      lowestMemberPrice: pricing.lowestMemberPrice ? applyChannelMarkupToCombinedRoomsAndRoundUpToTen(pricing.lowestMemberPrice, memberMarkupDetails, roomCount) : pricing.lowestMemberPrice,
      channelMarkupRoomCount: undefined,
    },
  }
}
